import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { createUseStyles } from 'react-jss'

import { stylesClasses, generateKey } from '../../utils/classes'
import { responsiveStyles } from '../../utils/styles'

import mediaQueries from '../../constants/mediaQueries'
const COLUMN_SIZES = ['auto', 'fill', true, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const OFFSET_SIZES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
const ORDER_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]

/**
 * Generate all the styles to be applied by JSS.
 * How-To use JSS styles:
 * <Component  customStyleProperty={key: value} />
 * [example]: <Modal width={sm: 100, md: 200, lg: 300} />
 */

/**
 * Returns object that stored, for each mediaQuery, desired key and its asociated CSS, for all sizes.
 * @param {string} mq - MediaQuery name.
 * @return {object} - All pairs {key: value}, where value is the CSS.
 */
function generateColWidthStyles(mq) {
	return COLUMN_SIZES.reduce((styles, size) => {
		const key = generateKey('col--', size, mq)


		switch (size) {
			case 'auto':
				styles[key] = responsiveStyles(mq, {
					flex: '0 0 auto',
					width: 'auto'
				})
				break
			case 'fill':
			case true:
				styles[key] = responsiveStyles(mq, {
					flex: '1 0 0%'
				})
				break
			default:
				styles[key] = responsiveStyles(mq, {
					flex: '0 0 auto',
					width: `${Math.round((size / 12) * 10e7) / 10e5}%`
				})
				break
		}
		/**
		 * returned styles object example: {
		 *      col--1: {flex: "0 0 auto", width: "8.333333%"},
		 *      col--md-auto: {@media screen and (min-width: 768px): {flex: "0 0 auto", width: "auto"}}
		 *      ...
		 *  }
		 */
		return styles
	}, {})
}

/**
 * Same behaviour as generateColWidthStyles()
 */
function generateColOffsetStyles(mq) {
	return OFFSET_SIZES.reduce((styles, size) => {
		const key = generateKey('col--offset-', size, mq)

		const marginLeft = `${Math.round((size / 12) * 10e7) / 10e5}%`

		styles[key] = responsiveStyles(mq, {
			marginLeft
		})

		return styles
	}, {})
}

/**
 * Same behaviour as generateColWidthStyles()
 */
function generateColOrderStyles(mq) {
	return ORDER_VALUES.reduce((styles, order) => {
		const key = generateKey('col--order-', order, mq)

		styles[key] = responsiveStyles(mq, { order })

		return styles
	}, {})
}

/**
 * Generate and merge all styles.
 */
const styles = {
	col: {
		flex: '1 0 0%'
	},
	...Object.keys(mediaQueries).reduce(
		(styles, mq) => ({
			...styles,
			...generateColWidthStyles(mq),
			...generateColOffsetStyles(mq),
			...generateColOrderStyles(mq)
		}),
		{}
	)
}
/**
 * Then generate the JSS with createUseStyles, adding prefix "Vb".
 */
const useStyles = createUseStyles(styles, { name: 'Vb' })

const Col = forwardRef(function Col(props, ref) {
	const {
		component: Component = 'div',
		className, // This prop is defined here to prevent its use. It's not allowed to override the column system.
		children,
		width,
		offset,
		order,
		...other
	} = props

	const classes = useStyles()

	// If customStyleProps are setted, stylesClasses() matches the key
	// with the unique CSS selector generated by the JSS.
	return (
		<Component
			ref={ref}
			className={clsx(
				classes['col'],
				width && stylesClasses(width, 'col--', classes),
				offset && stylesClasses(offset, 'col--offset-', classes),
				order && stylesClasses(order, 'col--order-', classes)
			)}
			{...other}
		>
			{children}
		</Component>
	)
})

export default Col

Col.defaultProps = {
	component: 'div'
}

Col.propTypes = {
	/**
	 * The content of the column.
	 */
	children: PropTypes.node,

	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * * @default 'div'
	 */
	component: PropTypes.elementType,

	/**
	 * The width of the column.
	 */
	width: (props, propName, componentName) => {
		const value = props[propName]

		if (typeof value === 'number') {
			return COLUMN_SIZES.includes(value)
				? null
				: new Error(`${propName} in ${componentName} is not a valid value. Valid values: ${COLUMN_SIZES.join(', ')}`)
		}

		if (typeof value === 'object') {
			let error = null

			const keys = Object.keys(value)

			keys.every((key) => {
				if (!mediaQueries[key]) {
					error = new Error(
						`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(mediaQueries).join(
							', '
						)}`
					)
					return false
				}
				if (!COLUMN_SIZES.includes(value[key])) {
					error = new Error(
						`${propName}.${key} in ${componentName} is not a valid value. Valid values: ${COLUMN_SIZES.join(', ')}`
					)
					return false
				}

				return true
			})

			if (error) {
				return error
			}
		}

		return null
	},

	/**
	 * The offset of the column.
	 */
	offset: (props, propName, componentName) => {
		const value = props[propName]

		if (typeof value === 'number') {
			return OFFSET_SIZES.includes(value)
				? null
				: new Error(`${propName} in ${componentName} is not a valid value. Valid values: ${OFFSET_SIZES.join(', ')}`)
		}

		if (typeof value === 'object') {
			let error = null

			const keys = Object.keys(value)

			keys.every((key) => {
				if (!mediaQueries[key]) {
					error = new Error(
						`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(mediaQueries).join(
							', '
						)}`
					)
					return false
				}
				if (!OFFSET_SIZES.includes(value[key])) {
					error = new Error(
						`${propName}.${key} in ${componentName} is not a valid value. Valid values: ${OFFSET_SIZES.join(', ')}`
					)
					return false
				}

				return true
			})

			if (error) {
				return error
			}
		}

		return null
	},

	/**
	 * The offset of the column.
	 */
	order: (props, propName, componentName) => {
		const value = props[propName]

		if (typeof value === 'number') {
			return ORDER_VALUES.includes(value)
				? null
				: new Error(`${propName} in ${componentName} is not a valid value. Valid values: ${ORDER_VALUES.join(', ')}`)
		}

		if (typeof value === 'object') {
			let error = null

			const keys = Object.keys(value)

			keys.every((key) => {
				if (!mediaQueries[key]) {
					error = new Error(
						`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(mediaQueries).join(
							', '
						)}`
					)
					return false
				}
				if (!ORDER_VALUES.includes(value[key])) {
					error = new Error(
						`${propName}.${key} in ${componentName} is not a valid value. Valid values: ${ORDER_VALUES.join(', ')}`
					)
					return false
				}

				return true
			})

			if (error) {
				return error
			}
		}

		return null
	}
}
